import Vue from 'vue';
import Vuex from 'vuex';
import _ from 'underscore';
import { RootState } from '@/types';
import api from '@/services/api';
import { JobCorpsCenter, IJobCorpsCenter } from './classes/JobCorpsCenter';
import UserProfile from './classes/UserProfile';
import Certification from './classes/Certification';
import { Certificate, ICertificate } from './classes/Certificate';
import CertificateForm from './classes/CertificateForm';
import Form from './classes/Form';
import { StudentProfile, IStudentProfile } from '@/classes/StudentProfile';
import { DocumentStore } from '@/views/documents/DocumentStore';
import { DocumentCategoryStore } from '@/store/DocumentCategoryStore';
import { StudentStore } from '@/views/students/StudentStore';
import { CertificateStore } from '@/store/CertificateStore';
import { CertificationStore } from '@/store/CertificationStore';
import { UserStore } from '@/store/UserStore';
import { MaterialStore } from '@/store/MaterialStore';
import { InjuryStore } from '@/store/InjuryStore';
import { MetaStore } from '@/store/MetaStore';
import { ContactStore } from '@/store/ContactStore';
import { NotificationsStore } from '@/store/NotificationsStore';
import MaterialForm from '@/classes/MaterialForm';
import InjuryForm from './classes/InjuryForm';

Vue.use(Vuex);

export default new Vuex.Store<RootState>({
  modules: {
    documents: DocumentStore,
    documentCategories: DocumentCategoryStore,
    students: StudentStore,
    certificates: CertificateStore,
    certifications: CertificationStore,
    users: UserStore,
    material: MaterialStore,
    injury: InjuryStore,
    meta: MetaStore,
    contact: ContactStore,
    notifications: NotificationsStore
  },

  state: {
    loggedIn: false,
    jobCorpsCenters: [],
    userProfiles: [],
    appEntryURL: '',
    activeProfile: undefined,
    certifications: [],
    certificateForms: [],
    certificates: []
  },
  mutations: {
    setLoggedIn(state: RootState, payload: boolean) {
      state.loggedIn = payload;
    },
    setJobCorpsCenters(state: RootState, payload: JobCorpsCenter[]) {
      payload.sort((centerA: JobCorpsCenter, centerB: JobCorpsCenter): number => {
        if (centerA.name !== undefined && centerA.name !== null && centerB.name !== undefined && centerB.name !== null) {
          return centerA.name.localeCompare(centerB.name);
        } else {
          return 0;
        }
      });
      state.jobCorpsCenters = payload;
    },
    setUserProfiles(state: RootState, payload: UserProfile[]) {
      payload.sort((userA: UserProfile, userB: UserProfile): number => {
        if (userA instanceof UserProfile && userB instanceof UserProfile) {
          return userA.getFullName().localeCompare(userB.getFullName());
        } else {
          return 0;
        }
      });
      state.userProfiles = payload;
    },
    setAppEntryURL(state: RootState, payload: string | undefined) {
      state.appEntryURL = payload;
    },
    setActiveProfile(state: RootState, payload: UserProfile | undefined) {
      state.activeProfile = payload;
    },
    setCertifications(state: RootState, payload: Certification[]) {
      payload.sort((certA: Certification, certB: Certification): number => {
        if (certA.name && certB.name) {
          return certA.name.localeCompare(certB.name);
        } else {
          return 0;
        }
      });
      state.certifications = payload;
    },
    setCertificateForms(state: RootState, payload: CertificateForm[]) {
      state.certificateForms = payload;
    },
    setCertificates(state: RootState, payload: Certificate[]) {
      payload.sort((certA: Certificate, certB: Certificate): number => {
        return certB.printingCompleteAsNumber - certA.printingCompleteAsNumber;
      });
      state.certificates = payload;
    }
  },
  actions: {
    loadCenters({ commit }) {
      return new Promise((resolve, reject) => {
        api()
          .get('centers')
          .then((response) => {
            const centers = response.data;
            const jobCorpsCenters: JobCorpsCenter[] = [];
            centers.forEach((element: IJobCorpsCenter) => {
              const jobCorpsCenter: JobCorpsCenter = new JobCorpsCenter(element);
              jobCorpsCenters.push(jobCorpsCenter);
            });
            commit('setJobCorpsCenters', jobCorpsCenters);
            resolve();
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    loadUserProfiles({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        api()
          .get('profiles')
          .then((response) => {
            const profiles = response.data;
            const userProfiles: UserProfile[] = [];
            profiles.forEach((element: any) => {
              const userProfile: UserProfile = UserProfile.userProfileFromAPI(element);
              userProfiles.push(userProfile);
            });
            commit('setUserProfiles', userProfiles);
            commit('users/setUserProfiles', userProfiles);
            dispatch('initializeUserProfile', true);
            resolve();
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    loadCertifications({ commit }) {
      return new Promise((resolve, reject) => {
        api()
          .get('certifications')
          .then((response) => {
            const certsJSON = response.data.certifications;
            const certifications: Certification[] = [];
            certsJSON.forEach((element: any) => {
              const certification: Certification = Certification.certificationFromApi(element);
              certifications.push(certification);
            });
            commit('setCertifications', certifications);
            commit('certifications/setCertifications', certifications);
            resolve();
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    loadCertificateForms({ commit, state }, forceRefresh: boolean) {
      return new Promise((resolve, reject) => {
        if (state.certificateForms.length === 0 || forceRefresh) {
          api()
            .get('certificaterequests')
            .then((response) => {
              const certForms = response.data;
              const certificateForms: CertificateForm[] = [];
              certForms.forEach((element: any) => {
                const certificateForm: CertificateForm = CertificateForm.certificateFormFromAPI(element);
                certificateForms.push(certificateForm);
              });
              commit('setCertificateForms', certificateForms);
              resolve();
            })
            .catch((e) => {
              reject(e);
            });
        } else {
          resolve();
        }
      });
    },
    loadCertificates({ commit, state, dispatch }, forceRefresh: boolean) {
      return new Promise((resolve, reject) => {
        if (state.certificateForms.length === 0 || forceRefresh) {
          const userProfiles = dispatch('loadUserProfiles');
          const certifications = dispatch('loadCertifications');
          const centers = dispatch('loadCenters');
          Promise.all([userProfiles, certifications, centers]).then(() => {
            api()
              .get('certificates')
              .then((response) => {
                if (response.data.success) {
                  const studentsJSON = response.data.students;
                  const students: StudentProfile[] = [];
                  studentsJSON.forEach((element: any) => {
                    const student: StudentProfile = new StudentProfile(element as IStudentProfile);
                    students.push(student);
                  });
                  commit('students/setStudents', students, { root: true });
                  const certificatesJSON = response.data.certificates;
                  const certificates: Certificate[] = [];
                  certificatesJSON.forEach((element: any) => {
                    const certificate: Certificate = new Certificate(element as ICertificate);
                    certificates.push(certificate);
                  });
                  commit('setCertificates', certificates);
                  commit('certificates/setCertificates', certificates);
                }
                resolve();
              })
              .catch((e) => {
                reject(e);
              });
          });
        } else {
          resolve();
        }
      });
    },
    initializeUserProfile({ commit, state }, forceRefresh: boolean) {
      return new Promise((resolve, reject) => {
        if (state.activeProfile === undefined || forceRefresh) {
          api()
            .get('profile')
            .then((response) => {
              const profile = response.data;
              const userProfile: UserProfile = UserProfile.userProfileFromAPI(profile);
              commit('setActiveProfile', userProfile);
              resolve();
            })
            .catch((e) => {
              reject(e);
            });
        } else {
          resolve();
        }
      });
    }
  },
  getters: {
    getLoggedIn: (state: RootState): boolean => {
      return state.loggedIn;
    },
    getUserProfileById:
      (state: RootState) =>
      (id: number | string): UserProfile | undefined => {
        const index: number = state.userProfiles.findIndex((user: UserProfile) => user.id === Number(id));
        if (index !== -1) {
          return state.userProfiles[index];
        } else {
          return undefined;
        }
      },
    getUserNameIndexById:
      (state: RootState) =>
      (id: number | string): number => {
        return state.userProfiles.findIndex((user: UserProfile) => user.id === Number(id));
      },
    getJobCorpsCenters: (state: RootState): JobCorpsCenter[] => {
      return _.sortBy(state.jobCorpsCenters as JobCorpsCenter[], (jc: JobCorpsCenter) => {
        return jc.name;
      });
    },
    getJobCorpsCentersAllowed: (state: RootState): JobCorpsCenter[] => {
      if (state.activeProfile && (state.activeProfile.securityLevel === 'FIELD_COORDINATOR' || state.activeProfile.securityLevel === 'INSTRUCTOR')) {
        return state.jobCorpsCenters
          .filter((center: JobCorpsCenter) => {
            if (state.activeProfile && state.activeProfile.jobCorpsCenterIds && center.id) {
              return state.activeProfile.jobCorpsCenterIds.includes(center.id);
            } else {
              return false;
            }
          })
          .sort((centerA: JobCorpsCenter, centerB: JobCorpsCenter): number => {
            if (centerA.name !== undefined && centerA.name !== null && centerB.name !== undefined && centerB.name !== null) {
              return centerA.name.localeCompare(centerB.name);
            } else {
              return 0;
            }
          });
      } else {
        return state.jobCorpsCenters.sort((centerA: JobCorpsCenter, centerB: JobCorpsCenter): number => {
          if (centerA.name !== undefined && centerA.name !== null && centerB.name !== undefined && centerB.name !== null) {
            return centerA.name.localeCompare(centerB.name);
          } else {
            return 0;
          }
        });
      }
    },
    getJobCorpsCenterById:
      (state: RootState) =>
      (id: number | string): JobCorpsCenter | undefined => {
        const index: number = state.jobCorpsCenters.findIndex((center: JobCorpsCenter) => center.id === Number(id));
        if (index !== -1) {
          return state.jobCorpsCenters[index];
        } else {
          return undefined;
        }
      },
    getJobCorpsCenterNameIndex:
      (state: RootState) =>
      (id: number | string): number => {
        return state.jobCorpsCenters.findIndex((center: JobCorpsCenter) => center.id === Number(id));
      },
    getJobCorpsCenterInstructorsById:
      (state: RootState) =>
      (id: number | string): UserProfile[] | undefined => {
        const centerIndex: number = state.jobCorpsCenters.findIndex((center: JobCorpsCenter) => center.id === Number(id));
        if (centerIndex !== -1) {
          const center: JobCorpsCenter = state.jobCorpsCenters[centerIndex];
          const instructorList: UserProfile[] = [];
          if (center.instructors) {
            center.instructors.forEach((i: number | string) => {
              const instructorIndex: number = state.userProfiles.findIndex((user: UserProfile) => user.id === Number(i));
              if (instructorIndex !== -1) {
                instructorList.push(state.userProfiles[instructorIndex]);
              }
            });
          }
          return instructorList;
        } else {
          return undefined;
        }
      },
    getInstructorList: (state: RootState) => {
      return state.userProfiles.filter((user: UserProfile) => user.securityLevel === 'INSTRUCTOR' && !user.override);
    },
    getFieldCoordinatorList: (state: RootState) => {
      return state.userProfiles.filter((user: UserProfile) => user.securityLevel === 'FIELD_COORDINATOR' && !user.override);
    },
    getAppEntryURL: (state: RootState): string | undefined => {
      return state.appEntryURL;
    },
    getActiveProfile: (state: RootState): UserProfile | undefined => {
      return state.activeProfile;
    },
    getSecurityLevel: (state: RootState): string | undefined => {
      return state.activeProfile ? state.activeProfile.securityLevel : undefined;
    },
    getUserProfileList: (state: RootState): UserProfile[] => {
      return state.userProfiles;
    },
    getCertifications: (state: RootState): Certification[] => {
      return state.certifications;
    },
    getCertificationById:
      (state: RootState) =>
      (id: number | string): Certification | undefined => {
        const index: number = state.certifications.findIndex((user: Certification) => user.id === Number(id));
        if (index !== -1) {
          return state.certifications[index];
        } else {
          return undefined;
        }
      },
    getCertificationNameIndexById:
      (state: RootState) =>
      (id: number | string): number => {
        return state.certifications.findIndex((user: Certification) => user.id === Number(id));
      },
    getCertificateForms: (state: RootState): CertificateForm[] => {
      return state.certificateForms !== undefined ? state.certificateForms : [];
    },
    getCertificates: (state: RootState): Certificate[] => {
      return state.certificates !== undefined ? state.certificates : [];
    },
    getUserProfileIdsByString:
      (state: RootState) =>
      (searchString: string): number[] => {
        if (searchString !== null && typeof searchString === 'string') {
          return state.userProfiles
            .filter((user: UserProfile) => user.getFullName().toLowerCase().includes(searchString.toLowerCase()))
            .map((us: UserProfile) => us.id as number);
        } else {
          return state.userProfiles === undefined ? [] : state.userProfiles.map((us: UserProfile): number => us.id as number);
        }
      },
    getUserProfileIdsByLastNameString:
      (state: RootState) =>
      (searchString: string | null): number[] => {
        if (searchString !== null && typeof searchString === 'string') {
          return state.userProfiles
            .filter((user: UserProfile) => user.getFullName().toLowerCase().includes(searchString.toLowerCase()))
            .map((us: UserProfile) => us.id as number);
        } else {
          return state.userProfiles === undefined ? [] : state.userProfiles.map((us: UserProfile): number => us.id as number);
        }
      },
    getUserProfileIdsByUbcIdString:
      (state: RootState) =>
      (searchString: string | null): number[] => {
        if (searchString !== null && typeof searchString === 'string') {
          return state.userProfiles
            .filter((user: UserProfile) => {
              if (user.ubcId !== undefined) {
                return user.ubcId.toLowerCase().includes(searchString.toLowerCase());
              } else {
                return false;
              }
            })
            .map((us: UserProfile) => us.id as number);
        } else {
          return state.userProfiles === undefined ? [] : state.userProfiles.map((us: UserProfile): number => us.id as number);
        }
      },
    getCertificationsIdsByName:
      (state: RootState) =>
      (searchString: string): number[] => {
        if (searchString !== null && typeof searchString === 'string') {
          return state.certifications
            .filter((certification: Certification) =>
              certification.name !== undefined ? certification.name.toLowerCase().includes(searchString.toLowerCase()) : false
            )
            .map((cert: Certification) => cert.id as number);
        } else {
          return state.certifications === undefined ? [] : state.certifications.map((cert: Certification): number => cert.id as number);
        }
      },
    getCertificateFormById:
      (state: RootState) =>
      (id: number): CertificateForm | undefined => {
        const index: number = state.certificateForms.findIndex((user: CertificateForm) => user.id === Number(id));
        if (index !== -1) {
          return state.certificateForms[index];
        } else {
          return undefined;
        }
      },
    getForms: (state: RootState, getters: any): Form[] => {
      const allForms: Form[] = [];
      const certForms = state.certificateForms !== undefined ? state.certificateForms : [];
      const matlForms = getters['material/getMaterialForms'] !== undefined ? (getters['material/getMaterialForms'] as Form[]) : [];
      const injrForms = getters['injury/getInjuryForms'] !== undefined ? (getters['injury/getInjuryForms'] as Form[]) : [];
      return allForms.concat(certForms, matlForms, injrForms);
    },
    getFormsInNeedOfAction: (state: RootState, getters: any): Form[] => {
      const activeProfile: UserProfile | undefined = state.activeProfile;
      if (activeProfile !== undefined) {
        const actionForms: Form[] = [];
        const matlForms = getters['material/getMaterialForms'] !== undefined ? getters['material/getMaterialForms'] : [];
        const matlFiltered = matlForms.filter((m: MaterialForm) => {
          if (activeProfile.securityLevel !== undefined && activeProfile.securityLevel === 'FIELD_COORDINATOR') {
            if (m.actionLevel === 'FIELD_COORDINATOR' && m.status === 'Pending_Review_by_Field_Coordinator') {
              return true;
            } else {
              return false;
            }
          } else if (activeProfile.securityLevel !== undefined && activeProfile.securityLevel === 'GENERAL_OFFICE') {
            if (m.status === 'Pending_Review_by_General_Office' && !m.requiresDirectorApproval) {
              return true;
            } else {
              return false;
            }
          } else if (activeProfile.securityLevel !== undefined && activeProfile.securityLevel === 'DIRECTOR') {
            if (m.actionLevel === 'DIRECTOR') {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        }) as Form[];
        const injrForms = getters['injury/getInjuryForms'] !== undefined ? getters['injury/getInjuryForms'] : [];
        const injrFiltered = injrForms.filter((i: InjuryForm) => {
          if (activeProfile.securityLevel !== undefined && activeProfile.securityLevel === 'INSTRUCTOR') {
            if (i.actionLevel === 'INSTRUCTOR' && i.status === 'Saved') {
              return true;
            } else {
              return false;
            }
          } else if (activeProfile.securityLevel !== undefined && activeProfile.securityLevel === 'FIELD_COORDINATOR') {
            if (i.actionLevel === 'FIELD_COORDINATOR' && i.status === 'Submitted') {
              return true;
            } else if (i.status === 'Saved' && i.submittedUserId === activeProfile.id) {
              return true;
            } else {
              return false;
            }
          } else if (
            activeProfile.securityLevel !== undefined &&
            (activeProfile.securityLevel === 'GENERAL_OFFICE' || activeProfile.securityLevel === 'DIRECTOR')
          ) {
            if (i.status === 'Reviewed' || i.status === 'Saved') {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        }) as Form[];
        return actionForms.concat(matlFiltered, injrFiltered);
      } else {
        return [];
      }
    },
    getAvailableUserList: (state: RootState): UserProfile[] => {
      if (state.activeProfile && (state.activeProfile.securityLevel === 'FIELD_COORDINATOR' || state.activeProfile.securityLevel === 'INSTRUCTOR')) {
        return state.userProfiles.filter((user: UserProfile) => {
          if (user.jobCorpsCenterIds && user.override !== true) {
            return user.jobCorpsCenterIds.some((centerId: number, idx, arr): boolean => {
              if (state.activeProfile && state.activeProfile.jobCorpsCenterIds) {
                return state.activeProfile.jobCorpsCenterIds.indexOf(centerId) >= 0;
              } else {
                return false;
              }
            });
          } else {
            return false;
          }
        });
      } else {
        return state.userProfiles;
      }
    }
  }
});
